import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';
import {
  CatalogItemShortDTO,
  FilterDTO,
  SearchRequest,
  FilterIdsDTO,
  FilterIdsDTOTypeEnum,
  SearchRequestSortByEnum,
  SearchRequestSortOrderEnum,
} from '@/api';
import services from '@/services';

type CatalogGetters = {
  total: number,
  items: CatalogItemShortDTO[],
  page: number,
  filters: FilterDTO[],
  loading: boolean,
  totalPages: number,
  perPage: number,
}

const ITEMS_PER_PAGE = 44;

type filters = {[value in FilterIdsDTOTypeEnum]?: string[]};
export interface SearchParams extends filters {
  page?: number;
  sortBy?: SearchRequestSortByEnum;
  sortOrder?: SearchRequestSortOrderEnum;
}

function searchParamsToSearchRequest(params: SearchParams): SearchRequest {
  const {
    sortBy, sortOrder, page, ...filters
  } = params;
  const filterIds = Object.entries(filters).map<FilterIdsDTO>((filter) => {
    const [key, value] = filter;
    return { type: key, ids: value } as FilterIdsDTO;
  }).filter(item => item.ids.length > 0);
  return {
    filterIds,
    offset: page && page > 0 ? ITEMS_PER_PAGE * (page - 1) : 0,
    length: ITEMS_PER_PAGE,
    sortBy,
    sortOrder,
  };
}

@Module({ name: 'CatalogModule' })
export default class CatalogModule extends VuexModule {
  perPage: number = ITEMS_PER_PAGE

  total: number = 0

  items: CatalogItemShortDTO[] = []

  page: number = 1

  filters: FilterDTO[] = []

  loading: boolean = false

  get catalog(): CatalogGetters {
    return {
      total: this.total,
      items: this.items,
      page: this.page,
      filters: this.filters,
      loading: this.loading,
      totalPages: this.totalPages,
      perPage: this.perPage,
    };
  }

  get allChildren() {
    return this.catalog.filters.flatMap(function loop(filter): FilterDTO[] {
      if (filter.children) return filter.children.flatMap(loop);
      return [filter];
    });
  }

  params: SearchParams = {
    GENDER: [],
    CATEGORY: [],
    COLOR: [],
    SUPPLIER: [],
    BRAND: [],
    // MATERIAL: [],
    ITEM_NAME: [],
    PRICE: [],
    page: 1,
    sortBy: SearchRequestSortByEnum.DATE,
    sortOrder: SearchRequestSortOrderEnum.DESC,
  }

  @Mutation
  updateParams(params: SearchParams) {
    Object.assign(this.params, params);
  }

  get totalPages(): number {
    return Math.ceil(+(this.total / this.perPage));
  }

  @Action({ rawError: true })
  async fetchItems(params: SearchParams) {
    this.context.commit('setLoadingState', true);
    try {
      const { data } = await services.catalogService()
        .catalogEndpointFindItems2(searchParamsToSearchRequest(params));
      this.setItems(data.result || []);
      this.setTotal(data.count || 0);
      amplitude.getInstance().logEvent('Загрузка списка товаров', params);
    } finally {
      this.context.commit('setLoadingState', false);
    }
  }

  @Action({ rawError: true })
  async applyFilters(filters: FilterDTO[]) {
    this.context.commit('SET_CURRENT_FILTERS', filters);
  }

  @Mutation
  setItems(items: CatalogItemShortDTO[]) {
    this.items = items;
  }

  @Mutation
  setTotal(total: number) {
    this.total = total;
  }

  @Mutation
  setLoadingState(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  SET_CURRENT_FILTERS(filters: FilterDTO[]) {
    this.filters = filters;
  }
}
